import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaCheckCircle, FaTimesCircle, FaUsers, FaCoins, FaSyncAlt, FaClock, FaBolt, FaHourglassHalf, FaGift } from 'react-icons/fa';
import { SiKick } from 'react-icons/si';

const COMMANDS = [
  {
    syntax: '!bağla <KOD>',
    alt: '!bagla <KOD>',
    who: 'Herkes',
    desc: 'Kullanıcı profilinden aldığı kodu chate yazarak Kick hesabını Saguex hesabına bağlar. Bağlandığı anda bekleyen tüm ödülleri de otomatik hesabına eklenir.'
  },
  {
    syntax: '!puanver <kullanıcı_adı> <miktar>',
    who: 'Yayıncı / Moderatör',
    desc: 'Belirli bir Kick kullanıcısına anında puan verir. Kullanıcı siteye bağlı değilse puan bekleyen ödül olarak kuyruğa alınır, bağlandığında otomatik eklenir.'
  },
  {
    syntax: '!puanver <kelime> <puan> <saniye>',
    who: 'Yayıncı / Moderatör',
    desc: 'Kelime yarışması başlatır: belirtilen süre boyunca chate o kelimeyi yazan HERKES (bir kez) puanı kazanır. Siteye bağlı olmayanların puanı bağlanınca eklenir.'
  },
  {
    syntax: '!yağmur <miktar>',
    alt: '!yagmur <miktar>',
    who: 'Yayıncı / Moderatör',
    desc: 'O anki yayında chate yazmış herkese kişi başı belirtilen miktarda puan dağıtır. Bağlı olmayanlar için bağlanınca otomatik eklenir.'
  }
];

const TABS = [
  { key: 'overview', label: 'Genel Bakış' },
  { key: 'sessions', label: 'Yayın Oturumları' },
  { key: 'commands', label: 'Komutlar' },
  { key: 'pending', label: 'Bekleyen Ödüller' }
];

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center gap-3 bg-navy-800/50 rounded-xl p-4 border border-gold/20">
    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${accent || 'bg-gold/15 text-gold'}`}>
      <Icon />
    </div>
    <div className="min-w-0">
      <div className="text-xs text-white/50 truncate">{label}</div>
      <div className="text-lg font-bold text-white truncate">{value}</div>
    </div>
  </div>
);

const KickManager = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [chatters, setChatters] = useState({});
  const [distributeAmount, setDistributeAmount] = useState({});
  const [distributing, setDistributing] = useState(null);
  const [commandLogs, setCommandLogs] = useState([]);
  const [giveaways, setGiveaways] = useState([]);
  const [pendingRewards, setPendingRewards] = useState({ rewards: [], total: 0, count: 0 });
  const [notice, setNotice] = useState(null);
  const [chatRewardForm, setChatRewardForm] = useState({ enabled: false, intervalMinutes: 10, amount: 5 });
  const [savingChatReward, setSavingChatReward] = useState(false);

  const token = localStorage.getItem('adminToken');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/kick/status', authHeaders);
      setStatus(res.data);
      if (res.data.chatReward) {
        setChatRewardForm(res.data.chatReward);
      }
    } catch (error) {
      console.error('Kick status fetch error:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveChatReward = async () => {
    setSavingChatReward(true);
    try {
      const res = await axios.put('/api/admin/kick/chat-reward-settings', chatRewardForm, authHeaders);
      setChatRewardForm(res.data);
      setStatus((prev) => (prev ? { ...prev, chatReward: res.data } : prev));
    } catch (error) {
      alert(error.response?.data?.error || 'Ayarlar kaydedilemedi');
    } finally {
      setSavingChatReward(false);
    }
  };

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/kick/sessions', authHeaders);
      setSessions(res.data);
    } catch (error) {
      console.error('Kick sessions fetch error:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCommandLogs = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/kick/commands', authHeaders);
      setCommandLogs(res.data);
    } catch (error) {
      console.error('Kick command logs fetch error:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchGiveaways = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/kick/giveaways', authHeaders);
      setGiveaways(res.data);
    } catch (error) {
      console.error('Kick giveaways fetch error:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPendingRewards = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/kick/pending-rewards', authHeaders);
      setPendingRewards(res.data);
    } catch (error) {
      console.error('Kick pending rewards fetch error:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('kick_connected')) {
      setNotice({ type: 'success', text: 'Kick hesabı başarıyla bağlandı!' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('kick_error')) {
      setNotice({ type: 'error', text: `Bağlantı hatası: ${params.get('kick_error')}` });
      window.history.replaceState({}, '', window.location.pathname);
    }
    fetchStatus();
    fetchSessions();
    fetchCommandLogs();
    fetchGiveaways();
    fetchPendingRewards();
  }, [fetchStatus, fetchSessions, fetchCommandLogs, fetchGiveaways, fetchPendingRewards]);

  const handleConnect = () => {
    window.location.href = `/api/admin/kick/connect?token=${token}`;
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Kick bağlantısını kesmek istediğinize emin misiniz? Yayın takibi ve chat komutları duracaktır.')) return;
    try {
      await axios.post('/api/admin/kick/disconnect', {}, authHeaders);
      fetchStatus();
    } catch (error) {
      alert(error.response?.data?.error || 'Bağlantı kesilemedi');
    }
  };

  const toggleSession = async (session) => {
    if (expandedSessionId === session.id) {
      setExpandedSessionId(null);
      return;
    }
    setExpandedSessionId(session.id);
    if (!chatters[session.id]) {
      try {
        const res = await axios.get(`/api/admin/kick/sessions/${session.id}/chatters`, authHeaders);
        setChatters((prev) => ({ ...prev, [session.id]: res.data }));
      } catch (error) {
        console.error('Chatters fetch error:', error);
      }
    }
  };

  const handleDistribute = async (session) => {
    const amount = parseInt(distributeAmount[session.id]);
    if (!amount || amount <= 0) {
      alert('Geçerli bir puan miktarı girin');
      return;
    }
    if (!window.confirm(`Bu oturumdaki tüm bağlı kullanıcılara ${amount} Sagu Coin dağıtılacak. Onaylıyor musunuz?`)) return;

    setDistributing(session.id);
    try {
      const res = await axios.post(`/api/admin/kick/sessions/${session.id}/distribute`, { amount }, authHeaders);
      alert(`${res.data.creditedCount} kullanıcıya ${amount} puan dağıtıldı. (${res.data.skippedCount} kullanıcı siteye bağlı değildi)`);
      fetchCommandLogs();
      setChatters((prev) => ({ ...prev, [session.id]: undefined }));
      if (expandedSessionId === session.id) toggleSession({ id: -1 });
    } catch (error) {
      alert(error.response?.data?.error || 'Dağıtım başarısız oldu');
    } finally {
      setDistributing(null);
    }
  };

  if (loading) {
    return <div className="text-white/60">Yükleniyor...</div>;
  }

  const activeSessionChatterCount = sessions.find((s) => s.id === status?.activeSession?.id)?.chatterCount ?? 0;

  return (
    <div className="max-w-5xl">
      <div className="flex items-center gap-3 mb-1">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold/15 text-gold text-xl">
          <SiKick />
        </div>
        <h2 className="text-2xl font-bold text-gold">Kick Entegrasyonu</h2>
      </div>
      <p className="text-white/60 text-sm mb-6">
        Kanalınızı Kick'e bağlayın: canlı yayında chate yazan kullanıcılar otomatik takip edilir, chat komutlarıyla anlık puan dağıtabilir,
        kullanıcılar profillerinden hesaplarını bağlayabilir.
      </p>

      {notice && (
        <div className={`p-4 rounded-lg border mb-6 ${notice.type === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}>
          {notice.text}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard
          icon={status?.connected ? FaCheckCircle : FaTimesCircle}
          label="Bağlantı Durumu"
          value={status?.connected ? `@${status.kickUsername || status.channelSlug}` : 'Bağlı Değil'}
          accent={status?.connected ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}
        />
        <StatCard icon={FaUsers} label="Aktif Oturum Chatter" value={status?.activeSession ? activeSessionChatterCount : '—'} />
        <StatCard icon={FaHourglassHalf} label="Bekleyen Ödül" value={`${pendingRewards.total} puan`} accent="bg-amber-500/15 text-amber-400" />
        <StatCard icon={FaBolt} label="Toplam Komut" value={commandLogs.length} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gold/20 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
              activeTab === tab.key ? 'border-gold text-gold' : 'border-transparent text-white/50 hover:text-white'
            }`}
          >
            {tab.label}
            {tab.key === 'pending' && pendingRewards.count > 0 && (
              <span className="ml-2 inline-flex items-center justify-center rounded-full bg-amber-500/20 text-amber-400 text-xs px-1.5 py-0.5">
                {pendingRewards.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="bg-navy-800/50 rounded-xl p-6 border border-gold/20">
            {!status?.configured && (
              <div className="mb-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm">
                <strong>Yapılandırma eksik:</strong> Sunucu ortam değişkenlerine <code>KICK_CLIENT_ID</code>, <code>KICK_CLIENT_SECRET</code> ve{' '}
                <code>KICK_REDIRECT_URI</code> eklemeniz gerekiyor. Bu bilgileri{' '}
                <a href="https://kick.com/settings/developer" target="_blank" rel="noopener noreferrer" className="underline">
                  kick.com/settings/developer
                </a>{' '}
                adresinden yeni bir uygulama oluşturarak alabilirsiniz.
              </div>
            )}

            {status?.connected ? (
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <FaCheckCircle className="text-green-400 text-2xl" />
                  <div>
                    <div className="text-white font-semibold">Bağlı: @{status.kickUsername || status.channelSlug}</div>
                    <div className="text-white/50 text-sm flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${status.channelIsLive ? 'bg-green-400 animate-pulse' : 'bg-white/20'}`}></span>
                      {status.channelIsLive ? 'Kanal şu anda canlı yayında' : 'Kanal şu anda yayında değil'}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleDisconnect}
                  className="bg-red-500/20 hover:bg-red-500/30 text-red-400 font-semibold px-4 py-2 rounded-lg transition-colors"
                >
                  Bağlantıyı Kes
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3 text-white/70">
                  <FaTimesCircle className="text-red-400 text-2xl" />
                  Henüz bir Kick kanalı bağlanmadı
                </div>
                <button
                  onClick={handleConnect}
                  disabled={!status?.configured}
                  className="flex items-center gap-2 bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-5 py-2.5 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <SiKick />
                  Kick ile Bağlan
                </button>
              </div>
            )}
          </div>

          <div className="bg-navy-800/50 rounded-xl p-6 border border-gold/20">
            <h3 className="text-lg font-bold text-white mb-1">Chat Aktivite Ödülü</h3>
            <p className="text-white/50 text-sm mb-4">
              Komut gerekmeden, sadece chat yaparak puan kazandırır: canlı yayın sırasında chate en az bir mesaj yazan
              her kullanıcı, belirlediğiniz süre dolduğunda otomatik puan kazanır. Yayın kapalıyken verilmez.
            </p>
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={chatRewardForm.enabled}
                  onChange={(e) => setChatRewardForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                  className="w-5 h-5 accent-gold"
                />
                <span className="text-white text-sm">Aktif</span>
              </label>
              <div>
                <label className="block text-xs text-white/50 mb-1">Kaç dakikada bir</label>
                <input
                  type="number"
                  min={1}
                  max={180}
                  value={chatRewardForm.intervalMinutes}
                  onChange={(e) => setChatRewardForm((prev) => ({ ...prev, intervalMinutes: parseInt(e.target.value) || 1 }))}
                  className="w-28 px-3 py-2 bg-navy-700 border border-gold/30 rounded text-white text-sm focus:outline-none focus:border-gold"
                />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">Kaç puan</label>
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={chatRewardForm.amount}
                  onChange={(e) => setChatRewardForm((prev) => ({ ...prev, amount: parseInt(e.target.value) || 1 }))}
                  className="w-28 px-3 py-2 bg-navy-700 border border-gold/30 rounded text-white text-sm focus:outline-none focus:border-gold"
                />
              </div>
              <button
                onClick={handleSaveChatReward}
                disabled={savingChatReward || !status?.connected}
                title={!status?.connected ? 'Önce bir Kick kanalı bağlayın' : undefined}
                className="bg-gradient-to-r from-gold to-yellow-500 text-navy-900 font-bold px-5 py-2.5 rounded-lg text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {savingChatReward ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          </div>

          <div className="bg-navy-800/50 rounded-xl p-6 border border-gold/20">
            <h3 className="text-lg font-bold text-white mb-4">Chat Komutları</h3>
            <div className="space-y-3">
              {COMMANDS.map((cmd) => (
                <div key={cmd.syntax} className="rounded-lg border border-white/10 bg-black/20 p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <code className="text-gold font-bold text-sm bg-gold/10 px-2 py-1 rounded">{cmd.syntax}</code>
                    {cmd.alt && <code className="text-gold/60 text-xs">{cmd.alt}</code>}
                    <span className="ml-auto text-[11px] uppercase tracking-wide text-white/40 bg-white/5 px-2 py-0.5 rounded-full">{cmd.who}</span>
                  </div>
                  <p className="text-white/60 text-sm">{cmd.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'sessions' && (
        <div className="bg-navy-800/50 rounded-xl p-6 border border-gold/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">Yayın Oturumları</h3>
            <button onClick={fetchSessions} className="text-white/50 hover:text-gold transition-colors">
              <FaSyncAlt />
            </button>
          </div>

          {sessions.length === 0 ? (
            <p className="text-white/50 text-sm">Henüz kaydedilmiş bir yayın oturumu yok.</p>
          ) : (
            <div className="space-y-3">
              {sessions.map((session) => (
                <div key={session.id} className="border border-gold/10 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleSession(session)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-navy-700/40 hover:bg-navy-700/60 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-2.5 h-2.5 rounded-full ${(session.isLive ?? session.is_live) ? 'bg-green-400 animate-pulse' : 'bg-white/20'}`}></span>
                      <span className="text-white text-sm">
                        {new Date(session.startedAt || session.started_at).toLocaleString('tr-TR')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-white/60 text-sm">
                      <FaUsers /> {session.chatterCount}
                    </div>
                  </button>

                  {expandedSessionId === session.id && (
                    <div className="p-4 bg-black/20">
                      <div className="flex items-end gap-2 mb-4">
                        <div className="flex-1">
                          <label className="block text-xs text-white/50 mb-1">Bu oturumdaki bağlı kullanıcılara puan dağıt</label>
                          <input
                            type="number"
                            value={distributeAmount[session.id] || ''}
                            onChange={(e) => setDistributeAmount((prev) => ({ ...prev, [session.id]: e.target.value }))}
                            placeholder="Örn: 50"
                            className="w-full px-3 py-2 bg-navy-700 border border-gold/30 rounded text-white text-sm focus:outline-none focus:border-gold"
                          />
                        </div>
                        <button
                          onClick={() => handleDistribute(session)}
                          disabled={distributing === session.id}
                          className="flex items-center gap-2 bg-gradient-to-r from-gold to-yellow-500 text-navy-900 font-bold px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                        >
                          <FaCoins /> {distributing === session.id ? 'Dağıtılıyor...' : 'Dağıt'}
                        </button>
                      </div>

                      {chatters[session.id] ? (
                        chatters[session.id].length === 0 ? (
                          <p className="text-white/40 text-sm">Chatte yazan kullanıcı kaydı yok.</p>
                        ) : (
                          <div className="max-h-64 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-white/40 text-left border-b border-white/10">
                                  <th className="py-1.5 font-normal">Kick Kullanıcı</th>
                                  <th className="py-1.5 font-normal">Mesaj</th>
                                  <th className="py-1.5 font-normal">Site Durumu</th>
                                </tr>
                              </thead>
                              <tbody>
                                {chatters[session.id].map((c) => (
                                  <tr key={c.kickUserId} className="border-b border-white/5">
                                    <td className="py-1.5 text-white">{c.kickUsername}</td>
                                    <td className="py-1.5 text-white/60">{c.messageCount}</td>
                                    <td className="py-1.5">
                                      {c.linked ? (
                                        <span className="text-green-400">Bağlı</span>
                                      ) : (
                                        <span className="text-white/30">Bağlı değil</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )
                      ) : (
                        <p className="text-white/40 text-sm">Yükleniyor...</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'commands' && (
        <div className="space-y-6">
          <div className="bg-navy-800/50 rounded-xl p-6 border border-gold/20">
            <h3 className="text-lg font-bold text-white mb-4">Chat Komut Geçmişi</h3>
            {commandLogs.length === 0 ? (
              <p className="text-white/50 text-sm">Henüz çalıştırılmış bir komut yok.</p>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-white/40 text-left border-b border-white/10">
                      <th className="py-1.5 font-normal">Komut</th>
                      <th className="py-1.5 font-normal">Kullanan</th>
                      <th className="py-1.5 font-normal">Hedef</th>
                      <th className="py-1.5 font-normal">Miktar</th>
                      <th className="py-1.5 font-normal">Durum</th>
                      <th className="py-1.5 font-normal">Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commandLogs.map((log) => (
                      <tr key={log.id} className="border-b border-white/5">
                        <td className="py-1.5 text-gold">{log.command}</td>
                        <td className="py-1.5 text-white">{log.issuerUsername || log.issuer_username}</td>
                        <td className="py-1.5 text-white/70">{log.targetUsername || log.target_username || '—'}</td>
                        <td className="py-1.5 text-white/70">{log.amount ?? '—'}{(log.affectedCount || log.affected_count) ? ` (${log.affectedCount || log.affected_count} kişi)` : ''}</td>
                        <td className="py-1.5">
                          <span className={log.status === 'success' ? 'text-green-400' : 'text-red-400'}>{log.status}</span>
                        </td>
                        <td className="py-1.5 text-white/40">
                          {new Date(log.createdAt || log.created_at).toLocaleString('tr-TR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-navy-800/50 rounded-xl p-6 border border-gold/20">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <FaClock className="text-gold" /> Kelime Yarışmaları
            </h3>
            {giveaways.length === 0 ? (
              <p className="text-white/50 text-sm">Henüz başlatılmış bir kelime yarışması yok.</p>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-white/40 text-left border-b border-white/10">
                      <th className="py-1.5 font-normal">Kelime</th>
                      <th className="py-1.5 font-normal">Puan</th>
                      <th className="py-1.5 font-normal">Katılan</th>
                      <th className="py-1.5 font-normal">Başladı</th>
                      <th className="py-1.5 font-normal">Bitiş</th>
                    </tr>
                  </thead>
                  <tbody>
                    {giveaways.map((g) => {
                      const expiresAt = new Date(g.expiresAt || g.expires_at);
                      const isActive = expiresAt.getTime() > Date.now();
                      return (
                        <tr key={g.id} className="border-b border-white/5">
                          <td className="py-1.5 text-gold font-semibold">{g.keyword}</td>
                          <td className="py-1.5 text-white/70">{g.amount}</td>
                          <td className="py-1.5 text-white/70">{g.claimCount}</td>
                          <td className="py-1.5 text-white/40">{new Date(g.startedAt || g.started_at).toLocaleTimeString('tr-TR')}</td>
                          <td className="py-1.5">
                            {isActive ? <span className="text-green-400">Aktif</span> : <span className="text-white/30">Bitti</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'pending' && (
        <div className="bg-navy-800/50 rounded-xl p-6 border border-gold/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <FaGift className="text-amber-400" /> Bekleyen Ödüller
            </h3>
            <button onClick={fetchPendingRewards} className="text-white/50 hover:text-gold transition-colors">
              <FaSyncAlt />
            </button>
          </div>
          <p className="text-white/50 text-sm mb-4">
            Chat komutlarıyla puan kazanan ama henüz Saguex hesabını bağlamamış Kick kullanıcıları burada listelenir.
            Hesaplarını <code className="text-gold/80">!bağla</code> ile bağladıkları anda puanlar otomatik eklenir.
          </p>
          {pendingRewards.rewards.length === 0 ? (
            <p className="text-white/40 text-sm">Bekleyen ödül yok.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/40 text-left border-b border-white/10">
                    <th className="py-1.5 font-normal">Kick Kullanıcı</th>
                    <th className="py-1.5 font-normal">Puan</th>
                    <th className="py-1.5 font-normal">Sebep</th>
                    <th className="py-1.5 font-normal">Tarih</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRewards.rewards.map((r) => (
                    <tr key={r.id} className="border-b border-white/5">
                      <td className="py-1.5 text-white">{r.kickUsername || r.kick_username}</td>
                      <td className="py-1.5 text-amber-400 font-semibold">{r.amount}</td>
                      <td className="py-1.5 text-white/50">{r.reason || '—'}</td>
                      <td className="py-1.5 text-white/40">{new Date(r.createdAt || r.created_at).toLocaleString('tr-TR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KickManager;
